import { Cell, Pie, PieChart, Tooltip } from 'recharts';

/**
 * MiniPieChart — a compact labeled pie for the dashboard stat row.
 *
 * Props:
 *   title   {string}  — question/heading shown above the chart
 *   data    {Array}   — [{ name, value, color }]
 *   loading {boolean}
 */

const WIDTH = 180;
const HEIGHT = 160;
const RADIAN = Math.PI / 180;

// Fallback palette for any entry that doesn't carry its own `color`.
const FALLBACK_COLORS = ['#002855', '#FFAE1E', '#4A90D9', '#6DB33F', '#C0392B', '#8E44AD'];

const boxStyle = {
  width: WIDTH,
  height: HEIGHT,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#999',
  fontSize: 12,
  background: '#f5f6f8',
  borderRadius: 6,
};

// Fixed two-line height so single-line titles (e.g. "Sentiment") reserve the
// same space as wrapping ones — keeps all the circles vertically aligned.
export const pieTitleStyle = {
  fontSize: 12,
  lineHeight: '16px',
  height: 32,
  color: '#666',
  textAlign: 'center',
  maxWidth: WIDTH,
  margin: '0 auto 4px',
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
};

// Percentage label drawn directly on each slice (white, bold). Slices under
// ~5% are skipped — the arc is too small for readable text; the tooltip still
// covers them on hover.
function renderSliceLabel({ cx, cy, midAngle, outerRadius, percent }) {
  if (percent < 0.05) return null;
  const r = outerRadius * 0.6;
  const x = cx + r * Math.cos(-midAngle * RADIAN);
  const y = cy + r * Math.sin(-midAngle * RADIAN);
  return (
    <text
      x={x}
      y={y}
      fill="#fff"
      fontSize={12}
      fontWeight="bold"
      textAnchor="middle"
      dominantBaseline="central"
    >
      {`${Math.round(percent * 100)}%`}
    </text>
  );
}

export default function MiniPieChart({ title, data, loading }) {
  const hasData = Array.isArray(data) && data.some((d) => (d.value || 0) > 0);
  const total = hasData ? data.reduce((sum, d) => sum + (d.value || 0), 0) : 0;

  return (
    <div style={{ width: WIDTH }}>
      <p style={pieTitleStyle}>{title}</p>
      {loading ? (
        <div style={boxStyle}>Loading…</div>
      ) : !hasData ? (
        <div style={boxStyle}>No data</div>
      ) : (
        <PieChart width={WIDTH} height={HEIGHT}>
          <Pie
            data={data}
            dataKey="value"
            nameKey="name"
            cx="50%"
            cy="50%"
            outerRadius={70}
            labelLine={false}
            label={renderSliceLabel}
            isAnimationActive={false}
          >
            {data.map((entry, index) => (
              <Cell
                key={entry.name}
                fill={entry.color || FALLBACK_COLORS[index % FALLBACK_COLORS.length]}
              />
            ))}
          </Pie>
          <Tooltip
            formatter={(value, name) => {
              const pct = total ? Math.round((value / total) * 100) : 0;
              return [`${value} (${pct}%)`, name];
            }}
          />
        </PieChart>
      )}
    </div>
  );
}
